import { Greyscale, ScreenSize } from 'glints-aries';
import { DropShadow } from 'glints-aries/lib/@next';
import {
  borderRadius4,
  borderRadius8,
} from 'glints-aries/lib/@next/utilities/borderRadius';
import { Neutral, Yellow } from 'glints-aries/lib/@next/utilities/colors';
import styled from 'styled-components';

import GlintsPicture from 'src/components/GlintsPicture';

import {
  CARD_SHADOW_HOVER,
  CONTENT_MAX_WIDTH,
  MOBILE_BREAKPOINT,
  SGGradients,
} from '../constants';

export const HeroSection = styled.section`
  position: relative;
  background: ${SGGradients.hero};
  color: ${Greyscale.white};
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background-image: radial-gradient(
      rgba(255, 255, 255, 0.18) 1px,
      transparent 1px
    );
    background-size: 22px 22px;
    -webkit-mask: linear-gradient(
      180deg,
      transparent,
      #000 30%,
      #000 70%,
      transparent
    );
    mask: linear-gradient(180deg, transparent, #000 30%, #000 70%, transparent);
    opacity: 0.6;
    pointer-events: none;
  }

  &::after {
    content: '';
    position: absolute;
    top: -20%;
    right: -10%;
    width: 520px;
    height: 520px;
    border-radius: 50%;
    background: radial-gradient(
      circle,
      rgba(255, 203, 52, 0.35),
      transparent 65%
    );
    pointer-events: none;

    @media (max-width: ${MOBILE_BREAKPOINT}px) {
      width: 320px;
      height: 320px;
    }
  }
`;

export const HeroInner = styled.div`
  position: relative;
  z-index: 2;
  max-width: ${CONTENT_MAX_WIDTH}px;
  margin: 0 auto;
  padding: 72px 24px 64px;
  display: grid;
  grid-template-columns: 1.1fr 0.9fr;
  gap: 48px;
  align-items: center;

  @media (max-width: ${ScreenSize.tablet}px) {
    grid-template-columns: 1fr;
    padding: 48px 24px;
    gap: 32px;
  }

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    padding: 36px 20px 40px;
    gap: 24px;
  }
`;

export const HeroCopy = styled.div`
  position: relative;
`;

export const Heading = styled.h1`
  font-size: 48px;
  /* Keep cloned accent backgrounds from overlapping adjacent lines. */
  line-height: 1.3;
  font-weight: 700;
  letter-spacing: -0.025em;
  margin: 0 0 24px;
  color: ${Greyscale.white};
  text-wrap: balance;

  @media (max-width: ${ScreenSize.desktopS}px) {
    font-size: 42px;
  }

  @media (max-width: ${ScreenSize.tablet}px) {
    font-size: 32px;
    line-height: 1.35;
    margin-bottom: 18px;
  }

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    font-size: 28px;
    line-height: 1.4;
  }
`;

export const HeadingAccent = styled.span`
  background: ${Yellow.Brand};
  color: ${Neutral.B18};
  padding: 0 8px;
  border-radius: ${borderRadius4};
  -webkit-box-decoration-break: clone;
  box-decoration-break: clone;
`;

export const Lede = styled.p`
  font-size: 18px;
  line-height: 1.6;
  color: rgba(255, 255, 255, 0.92);
  margin: 0 0 32px;
  max-width: 560px;

  @media (max-width: ${ScreenSize.tablet}px) {
    font-size: 16px;
    margin-bottom: 24px;
  }

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    font-size: 15px;
    margin-bottom: 20px;
  }
`;

export const CTARow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    gap: 10px;

    & > * {
      flex: 1 1 auto;
      justify-content: center;
    }
  }
`;

const ctaBase = `
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 14px 22px;
  border-radius: ${borderRadius8};
  font-weight: 700;
  font-size: 15px;
  min-height: 48px;
  cursor: pointer;
  text-decoration: none;
  transition: transform 0.15s ease, box-shadow 0.15s ease, background 0.15s ease;
  font-family: inherit;
  border: 0;

  &:hover {
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }

  &:focus-visible {
    outline: 2px solid ${Greyscale.white};
    outline-offset: 3px;
  }

  @media (prefers-reduced-motion: reduce) {
    transition: background 0.15s ease;

    &:hover {
      transform: none;
    }
  }
`;

export const PrimaryCTA = styled.a`
  ${ctaBase}
  background: ${Yellow.Brand};
  color: ${Neutral.B18};
  box-shadow: ${DropShadow.card};

  &:hover {
    background: ${Yellow.S87};
    box-shadow: ${CARD_SHADOW_HOVER};
  }
`;

export const SecondaryCTA = styled.a`
  ${ctaBase}
  background: rgba(255, 255, 255, 0.14);
  color: ${Greyscale.white};
  border: 1.5px solid rgba(255, 255, 255, 0.55);
  backdrop-filter: blur(4px);

  &:hover {
    background: rgba(255, 255, 255, 0.22);
    border-color: ${Greyscale.white};
  }
`;

/* Right-side illustration */

export const HeroArt = styled.div`
  position: relative;
  justify-self: end;
  width: 100%;
  max-width: 520px;

  @media (max-width: ${ScreenSize.tablet}px) {
    justify-self: center;
    max-width: 380px;
  }

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    max-width: 280px;
  }
`;

export const HeroImage = styled(GlintsPicture)`
  width: 100%;
  height: auto;
  display: block;
  filter: drop-shadow(0 24px 48px rgba(0, 0, 0, 0.25));
`;
